import { AfterContentInit, Component, Input, OnDestroy } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { of } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';

import { CredentialsService, untilDestroyed } from '@app/core';
import { AuthenticationMethod } from '@app/models';
import { ConfirmOtpModalService } from '@app/shared/confirm-otp-modal/confirm-otp-modal.service';
import { ErrorHandlerService, NotyService, PhoneOtpProviderService, PhoneOtpProvider } from '@app/services';
import { LoaderService } from '@app/shared/loader/loader.service';
import { CountdownTimerService } from '@app/services/countdowntimer.service';
import { AuthenticationService } from '@app/services/authentication.service';
import { UserService } from '@app/services/user.service';
import { EnterpriseService } from '@app/services/enterprise.service';

export interface ModalResult {
  accessToken: string;
  phoneNumber: string;
  authenticationMethod: AuthenticationMethod;
}
export interface ModalPublicProperties {
  type?: AuthenticationMethod;
  forceUserCreation?: boolean;
  stopAutoLogin?: boolean;
  phoneNumber?: string;
}

@Component({
  selector: 'app-sms-login-modal',
  templateUrl: './sms-login-modal.component.html',
  styleUrls: ['./sms-login-modal.component.scss'],
  standalone: false
})
export class SmsLoginModalComponent implements AfterContentInit, ModalPublicProperties, OnDestroy {
  @Input() type: AuthenticationMethod;
  @Input() forceUserCreation = false;
  @Input() stopAutoLogin = false;
  @Input() phoneNumber: string;
  form: UntypedFormGroup;
  isLoading = false;
  token = new FormControl('');
  isVerifyingToken = false;
  isInvalidToken = false;
  verifyPhoneExisting = false;
  formattedTime = '';
  isStaticPhoneNumber = false;
  currentAccessToken: string = null;
  verificationTimer = 20000;
  currentPhoneOtpProvider!: PhoneOtpProvider;
  readonly AuthenticationMethod = AuthenticationMethod;

  constructor(
    public modal: NgbActiveModal,
    public timerService: CountdownTimerService,
    public enterpriseService: EnterpriseService,
    private formBuilder: UntypedFormBuilder,
    private notyService: NotyService,
    private errorHandlerService: ErrorHandlerService,
    private loaderService: LoaderService,
    public phoneOtpProvider: PhoneOtpProviderService,
    private authenticationService: AuthenticationService,
    private credentialsService: CredentialsService,
    private userService: UserService,
    private confirmOtpModalService: ConfirmOtpModalService
  ) {
    this.form = this.formBuilder.group({ phoneNumber: '', code: '' });
  }

  ngOnDestroy() {}

  ngAfterContentInit() {
    this.currentPhoneOtpProvider = this.phoneOtpProvider.primaryProviderForPhone(this.phoneNumber);
    this.type = this.phoneOtpProvider.authenticationMethod(this.currentPhoneOtpProvider);

    if (this.phoneNumber) {
      this.form.get('phoneNumber').setValue(this.phoneNumber);
      this.isStaticPhoneNumber = true;
      this.sendSms();
      this.timeOutForVerification();
    } else {
      setTimeout(() => (document.querySelector('app-sms-login-modal input[name=phoneNumber]') as HTMLInputElement).focus());
    }
  }

  sendSms(provider?: PhoneOtpProvider) {
    this.phoneNumber = this.form.get('phoneNumber').value;
    this.currentPhoneOtpProvider = this.phoneOtpProvider.normalizeProviderForPhone(this.phoneNumber, provider);
    this.type = this.phoneOtpProvider.authenticationMethod(this.currentPhoneOtpProvider);
    this.isVerifyingToken = true;
    this.isInvalidToken = false;
    this.currentAccessToken = null;
    this.loaderService.show();
    this.phoneOtpProvider
      .sendVerification(this.phoneNumber, this.forceUserCreation, this.currentPhoneOtpProvider)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.verifyPhoneExisting = true;
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          setTimeout(() => (this.isVerifyingToken = false), this.verificationTimer);
          this.notyService.success(this.phoneOtpProvider.translation('notification', this.currentPhoneOtpProvider, { instant: true }));
        },
        error: error => {
          setTimeout(() => (this.isVerifyingToken = false), 2000);
          this.loaderService.hide();
          this.errorHandlerService.handle(error);
        }
      });
  }

  requestCode() {
    this.sendSms();
  }

  requestFallbackCode() {
    const fallbackProvider = this.phoneOtpProvider.fallbackProvider(this.currentPhoneOtpProvider);

    if (fallbackProvider) {
      this.sendSms(fallbackProvider);
    }
  }

  submitToken() {
    this.isVerifyingToken = true;
    this.isInvalidToken = false;
    this.isLoading = true;

    this.type = this.phoneOtpProvider.authenticationMethod(this.currentPhoneOtpProvider);

    const phoneAuth$ = this.phoneOtpProvider.submitVerificationCode(this.phoneNumber, this.token.value, this.currentPhoneOtpProvider).pipe(
      tap(response => (this.currentAccessToken = response.access_token)),
      map(() => this.currentAccessToken)
    );

    // if otp is dismissed, avoids the need to rerun the sms/whatsapp login
    (this.isPartialLogin() ? of(this.currentAccessToken) : phoneAuth$)
      .pipe(
        switchMap(accessToken => {
          if (this.stopAutoLogin) {
            return of(accessToken);
          } else {
            return (this.isPartialLogin() ? of(this.credentialsService.credentials) : this.authenticationService.login({ code: accessToken, phone_number: this.phoneNumber }, this.type)).pipe(
              switchMap(credentials => (credentials.mfa.length > 0 ? this.confirmOtpModalService.open({ logoutOnDismiss: true }) : of(credentials))),
              switchMap(() => this.userService.getCurrentUser({ fetchPolicy: 'network-only' })),
              map(() => accessToken)
            );
          }
        }),
        tap({
          next: () => (this.isVerifyingToken = false),
          error: () => (this.isVerifyingToken = false)
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: accessToken => this.modal.close({ accessToken, phoneNumber: this.phoneNumber, authenticationMethod: this.type } as ModalResult),
        error: () => (this.isInvalidToken = true)
      });
  }

  timeOutForVerification() {
    if (this.timerService.isCountdownInProgress()) {
      this.timerService.stopCountdown();
    }
    this.timerService
      .startCountdownTimer()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: time => {
          this.formattedTime = time;
        }
      });
  }

  get formattedPhone() {
    return this.maskPhone(this.form.get('phoneNumber').value);
  }

  hasPhoneOtpFallbackProvider() {
    return !this.enterpriseService.hideForEnterprise() && this.phoneOtpProvider.hasFallbackProvider(this.currentPhoneOtpProvider);
  }

  phoneOtpFallbackProviderLabel() {
    const fallbackProvider = this.phoneOtpProvider.fallbackProvider(this.currentPhoneOtpProvider);
    return fallbackProvider ? this.phoneOtpProvider.providerLabel(fallbackProvider) : '';
  }

  maskPhone(phone: string): string {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    return digits.replace(/^(\d{2})(\d{2})(\d{5})(\d{4}).*/, '+$1 ($2) $3-$4');
  }

  private isPartialLogin() {
    return !!this.currentAccessToken && this.authenticationService.isMissingTwoStep();
  }
}
